import { useTitle } from "@/hooks/use-title.hook";
import { useUnsavedChangesBlocker } from "@/hooks/use-unsaved-changes-blocker.hook";
import {
  useEmployeePrefill,
  useEmployeeMe,
  useEmployeeHire,
  useEmployeeHireJobStatus,
} from "@/queries/employees/use-employees.query";
import { useHiringFlows } from "@/queries/use-hiring-flows.query";
import { useJobProfileById } from "@/queries/use-job-profiles.query";
import { useOrganizationSettings } from "@/queries/settings/use-organization.query";
import usePermissions from "@/hooks/use-permissions.hook";
import { ApiPermission } from "@/types/role.types";
import NotFound from "@/pages/NotFound";
import { getHireSteps, HireJobState, HireStep } from "./hire-context";

import {
  PrefillCandidate,
  ApiEmployeeHireJobQueued,
  ProcessingJobStatus,
} from "@/types/employees/employee-hire.types";
import { HireProvider } from "./hire-edit-pages";
import {
  buildHirePayload,
  emptyHirePackage,
  getHireOnboardingDefaults,
  getHirePackageColumns,
  getHireSections,
  mainFieldNames,
  personalFieldNames,
  HireFieldValues,
  HireOnboardingValues,
  HirePackageState,
} from "./hire-form";
import { HireFirstStep } from "./components/HireFirstStep";
import { HirePersonalStep } from "./components/HirePersonalStep";
import { HireOnboardingStep } from "./components/HireOnboardingStep";
import { HireFinalStep } from "./components/HireFinalStep";
import { HireStepLoading } from "./components/HireStepLoading";
import { getHireCompensationPrefill } from "./components/hire-compensation-prefill";
import { ApiEmployee } from "@/types/employees/employee.types";
import { ApiHiringFlow } from "@/types/hiring-flow.types";
import { ApiFieldTypes, ApiFieldValueTypes, ApiTableTypes } from "@/types/custom-field.types";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook";
import { ApiError, ApiErrors, ApiValidationError } from "@/types/api.types";
import { Button, FullScreenPage, Text } from "@skailer-com/uikit";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

export function HirePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const requestedId = Number(searchParams.get("hiring_flow_id"));
  const candidateId = Number(searchParams.get("candidate_id")) || undefined;
  const flowsQuery = useHiringFlows();
  const prefillQuery = useEmployeePrefill(candidateId);
  const navigate = useNavigate();
  const flow = flowsQuery.data?.find((item) =>
    requestedId ? item.id === requestedId : item.is_default
  );

  useTitle(t("core:new_employee"));

  if (flowsQuery.isSuccess && !flow) return <NotFound />;
  if (!flow || prefillQuery.isLoading) {
    if (flowsQuery.isError || prefillQuery.isError) {
      return (
        <FullScreenPage>
          <Text>{t("hiring_flow:not_found")}</Text>
          <Button onClick={() => navigate("/employees")}>{t("common:back")}</Button>
        </FullScreenPage>
      );
    }
    return <HireStepLoading />;
  }

  return (
    <HireWizard
      key={flow.id}
      flow={flow}
      prefillData={prefillQuery.data}
      candidateId={candidateId}
      isPreview={false}
    />
  );
}

export function HireWizard({
  flow,
  prefillData,
  candidateId,
  isPreview,
  onExitPreview,
}: {
  flow: ApiHiringFlow;
  prefillData?: PrefillCandidate;
  candidateId?: number;
  isPreview: boolean;
  onExitPreview?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { data: me } = useEmployeeMe();
  const { canIView } = usePermissions();
  const organizationQuery = useOrganizationSettings(canIView(ApiPermission.settings_organization));
  const personnelNumberAuto = organizationQuery.data
    ? Boolean(organizationQuery.data.personnel_number_auto_enabled)
    : undefined;
  const steps = getHireSteps(flow, personnelNumberAuto);
  const lastStep = steps[steps.length - 1];
  const toVisibleStep = (target: HireStep) => (steps.includes(target) ? target : lastStep);
  const [step, setStep] = useState(HireStep.create);
  const defaultCurrency =
    prefillData?.offer?.salary?.currency?.code ?? me?.organization.currency ?? "";
  const compensationDefaults = useMemo(() => {
    const salary = getHireCompensationPrefill(
      prefillData?.offer?.salary?.salary_type,
      prefillData?.offer?.salary?.salary_value
    );
    return {
      ...(salary.base_salary_gross != null ? { amount_gross: salary.base_salary_gross } : {}),
      ...(salary.base_salary_net != null ? { amount_net: salary.base_salary_net } : {}),
      ...(salary.per_type ? { per_type: salary.per_type } : {}),
      ...(prefillData?.offer?.salary?.currency?.code
        ? { currency_code: prefillData.offer.salary.currency.code }
        : {}),
    };
  }, [prefillData]);
  const [compensation, setCompensation] = useState<HirePackageState>(emptyHirePackage);
  const { fields: compensationTableFields } = useSystemTableConfig(
    ApiTableTypes.employee_compensations
  );
  const afterProbationColumns = getHirePackageColumns(compensationTableFields);
  const [mainValues, setMainValues] = useState<HireFieldValues>(() => {
    const defaults: HireFieldValues = {
      first_name: prefillData?.applicant.first_name ?? "",
      last_name: prefillData?.applicant.last_name ?? "",
      middle_name: prefillData?.applicant.middle_name ?? "",
      date_hired_on: prefillData?.offer?.date_start
        ? new Date(prefillData.offer.date_start)
        : new Date(),
    };
    const prefill: Record<string, unknown> = {
      department_id: prefillData?.job?.department?.id,
      division_id: prefillData?.job?.division?.id,
      job_profile_id: prefillData?.job?.job_profile?.id,
      location_id: prefillData?.offer?.location?.id,
      legal_entity_id: prefillData?.offer?.legal_entity?.id,
      reporting_to_id: prefillData?.offer?.manager?.id,
    };
    for (const section of getHireSections(flow.form.main))
      for (const field of section.fields) {
        const name = mainFieldNames[field.type];
        const value = name ? prefill[name] : undefined;
        if (value != null)
          defaults[field.slug] = [
            ApiFieldValueTypes.select,
            ApiFieldValueTypes.text_array,
            ApiFieldValueTypes.employee_reference,
            ApiFieldValueTypes.entity_reference,
          ].includes(field.field_type)
            ? [value]
            : value;
      }
    return defaults;
  });
  const avatarUrl = mainValues.avatar_url;
  useEffect(
    () => () => {
      if (typeof avatarUrl === "string" && avatarUrl.startsWith("blob:"))
        URL.revokeObjectURL(avatarUrl);
    },
    [avatarUrl]
  );
  const [personalValues, setPersonalValues] = useState<HireFieldValues>(() => {
    const defaults: HireFieldValues = {};
    for (const section of getHireSections(flow.form.personal))
      for (const field of section.fields) {
        if (field.type === ApiFieldTypes.employee_email_personal)
          defaults[field.slug] = prefillData?.applicant.contacts?.email ?? "";
        if (field.type === ApiFieldTypes.employee_phone_personal)
          defaults[field.slug] = prefillData?.applicant.contacts?.phone ?? "";
      }
    return defaults;
  });
  const [onboarding, setOnboarding] = useState(() => getHireOnboardingDefaults(flow));
  const getDraftSnapshot = () =>
    JSON.stringify({
      mainValues,
      personalValues,
      onboarding: { ...onboarding, compensation: null },
    });
  const [initialState] = useState(getDraftSnapshot);
  const [errors, setErrors] = useState<Partial<Record<HireStep, Record<string, string>>>>({});
  const [completed, setCompleted] = useState(false);
  const [hireJobState, setHireJobState] = useState<HireJobState>("idle");
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const unmounted = useRef(false);
  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  const submitting = useRef(false);
  const hire = useEmployeeHire();
  const [queuedJob, setQueuedJob] = useState<ApiEmployeeHireJobQueued | null>(null);
  const hireJob = useEmployeeHireJobStatus(queuedJob);
  useEffect(() => {
    const handleStatus = () => {
      if (!queuedJob) return;
      if (hireJob.isError) {
        setHireJobState("unknown");
        setQueuedJob(null);
        return;
      }
      if (hireJob.data?.status === ProcessingJobStatus.completed) {
        setQueuedJob(null);
        if (hireJob.data.employee_id) {
          void client.invalidateQueries({
            queryKey: ["employees"],
            predicate: (query) => query.queryKey[1] !== "hireJobStatus",
          });
          navigate(`/employees/${hireJob.data.employee_id}`, { replace: true });
        } else setHireJobState("unknown");
      } else if (hireJob.data?.status === ProcessingJobStatus.failed) {
        setQueuedJob(null);
        setHireJobState("idle");
        setCompleted(false);
        const code = hireJob.data.error_code;
        setErrors({
          [lastStep]: {
            server_error:
              code && code !== "employee_hire_failed"
                ? t(`api_errors:${code}`, { defaultValue: t("hiring_flow:hire_job_failed") })
                : t("hiring_flow:hire_job_failed"),
          },
        });
        setStep(lastStep);
      }
    };
    handleStatus();
  }, [queuedJob, hireJob.data, hireJob.isError, client, navigate, t, lastStep]);
  const jobProfileField = getHireSections(flow.form.main)
    .flatMap((section) => section.fields)
    .find((field) => field.type === ApiFieldTypes.employee_position_job_profile);
  const jobProfileValue = jobProfileField ? mainValues[jobProfileField.slug] : undefined;
  const jobProfileId =
    Number(Array.isArray(jobProfileValue) ? jobProfileValue[0] : jobProfileValue) || 0;
  const { data: jobProfile } = useJobProfileById(jobProfileId);
  const employee = {
    id: 0,
    first_name: String(mainValues.first_name ?? ""),
    last_name: String(mainValues.last_name ?? ""),
    middle_name: String(mainValues.middle_name ?? ""),
    avatar_url: mainValues.avatar_url ?? null,
    custom_fields: [],
    employee_field_permissions: {},
    date_hired_on: mainValues.date_hired_on,
    employee_position: {
      job_profile: jobProfileField
        ? jobProfileId
          ? jobProfile
          : null
        : prefillData?.job?.job_profile,
      division: prefillData?.job?.division,
      department: prefillData?.job?.department,
      manager: prefillData?.offer?.manager,
    },
  } as unknown as ApiEmployee;

  const handleHireError = (apiError?: ApiError) => {
    const nextErrors: Partial<Record<HireStep, Record<string, string>>> = {};

    if (apiError?.status === ApiErrors.validationError) {
      for (const error of (apiError as ApiValidationError).validationErrors) {
        const path = error.field.replace(/^body\./, "");
        const message =
          error.user_message ||
          t(`api_errors:${error.code}`, { defaultValue: t("hiring_flow:hire_failed") });
        const packageBlock = (["compensation", "compensation_after_probation"] as const).find(
          (block) => path === block || path.startsWith(`${block}.`)
        );

        if (packageBlock) {
          const packageStep =
            packageBlock === "compensation" ? HireStep.create : toVisibleStep(HireStep.onboarding);
          const packagePath = path.slice(packageBlock.length + 1);
          const columnType =
            packagePath === "change_reason_id"
              ? ApiFieldTypes.employee_compensation_change_reason
              : packagePath === "comment"
                ? ApiFieldTypes.employee_compensation_comment
                : undefined;
          const column = (
            packageBlock === "compensation"
              ? getHireSections(flow.form.main).flatMap((section) => section.fields)
              : afterProbationColumns
          ).find((item) =>
            columnType
              ? item.type === columnType
              : item.type === ApiFieldTypes.custom &&
                item.slug === packagePath.replace(/^custom_fields\./, "")
          );
          const key = column?.slug ?? packageBlock;
          nextErrors[packageStep] = {
            ...nextErrors[packageStep],
            [key]: [nextErrors[packageStep]?.[key], message].filter(Boolean).join(" "),
          };
          continue;
        }

        let targetStep = lastStep;
        let name = path;

        for (const group of ["main", "personal"] as const) {
          const fieldName = path
            .replace(/^(main|personal|custom_fields)\./, "")
            .replace(/^custom_fields\./, "");
          const field = getHireSections(flow.form[group])
            .flatMap((section) => section.fields)
            .find(
              (item) =>
                item.slug === fieldName ||
                (group === "main" ? mainFieldNames : personalFieldNames)[item.type] === fieldName
            );

          if (field && (!path.startsWith("personal.") || group === "personal")) {
            targetStep = group === "main" ? HireStep.create : toVisibleStep(HireStep.personal);
            name =
              group === "main" && ["first_name", "last_name", "middle_name"].includes(fieldName)
                ? fieldName
                : field.slug;
            break;
          }

          if (
            group === "main" &&
            ["first_name", "last_name", "middle_name", "date_hired_on"].includes(fieldName)
          ) {
            targetStep = HireStep.create;
            name = fieldName;
            break;
          }
        }

        nextErrors[targetStep] = {
          ...nextErrors[targetStep],
          [name]: message,
        };
      }
    }

    if (!Object.keys(nextErrors).length) {
      nextErrors[lastStep] = { server_error: t("hiring_flow:hire_failed") };
    }

    setErrors(nextErrors);
    setStep(steps.find((item) => nextErrors[item]) ?? lastStep);
  };

  const complete = async (
    values: HireOnboardingValues,
    fieldValues: Partial<Record<"main" | "personal", HireFieldValues>> = {}
  ) => {
    if (submitting.current || queuedJob) return;

    setOnboarding(values);

    if (isPreview) {
      setCompleted(true);
      setStep(HireStep.final);
      return;
    }

    submitting.current = true;
    setErrors({});

    try {
      const result = await hire.mutateAsync({
        ...buildHirePayload(
          flow,
          fieldValues.main ?? mainValues,
          fieldValues.personal ?? personalValues,
          values,
          candidateId,
          compensation,
          afterProbationColumns
        ),
        idempotency_key: idempotencyKey,
      });

      if (unmounted.current) return;
      if (!result.data) {
        handleHireError(result.error);
        return;
      }

      setCompleted(true);
      setHireJobState("pending");
      setStep(HireStep.final);

      setQueuedJob(result.data);
    } catch {
      if (!unmounted.current) handleHireError();
    } finally {
      submitting.current = false;
    }
  };

  if (organizationQuery.isLoading) return <HireStepLoading />;

  return (
    <HireProvider
      value={{
        employee,
        flow,
        mainValues,
        personalValues,
        onboarding,
        setMainValues,
        setPersonalValues,
        setOnboarding,
        compensation,
        setCompensation,
        compensationDefaults,
        defaultCurrency,
        step,
        steps,
        goToStep: setStep,
        personnelNumberAuto,
        isPreview,
        isSubmitting: hire.isPending || queuedJob !== null,
        hireJobState,
        complete,
        errors,
        prefillData,
        candidateId,
        exit: () =>
          isPreview && onExitPreview
            ? onExitPreview()
            : navigate(isPreview ? `/settings/hiring-flows/${flow.id}` : "/employees"),
      }}
    >
      {!isPreview && (
        <HireNavigationGuard dirty={!completed && getDraftSnapshot() !== initialState} />
      )}
      {step === HireStep.create ? (
        <HireFirstStep key={flow.id} />
      ) : step === HireStep.personal ? (
        <HirePersonalStep key={flow.id} />
      ) : step === HireStep.onboarding ? (
        <HireOnboardingStep key={flow.id} />
      ) : (
        <HireFinalStep />
      )}
    </HireProvider>
  );
}

function HireNavigationGuard({ dirty }: { dirty: boolean }) {
  useUnsavedChangesBlocker(dirty);
  return null;
}
